import React, { useMemo } from 'react';
import { AccordionDetails } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  TimesheetLabelSuggestionContextProvider,
  LabelsFormFieldContextProvider
} from 'modules/timesheet-labels';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';
import { TimeSignalAttribute, TimeSignal } from 'graphql/types';
import {
  AttributesContent,
  TimerContextProvider,
  FormErrorDialogContextProvider,
  NoteSourcesTable
} from 'modules/notes/common';
import { CustomLabels } from 'modules/customLabels/components/CustomLabels';
import { BulkSuggestionAcceptAllActions } from 'modules/timesheet-labels/BulkSuggestionAcceptAllActions';
import { SplitEntry, SPLIT_NOTE_FIELDS } from '../../types';
import { useSplitEntriesContext } from '../../SplitEntriesProvider';
import { SplitEntryDescription } from './SplitEntryDescription';
import { DateTimeContent } from './DateTimeContent';
import TimesheetLabelsSplitContent from './TimesheetLabelsSplitContent';

type SplitEntryAccordionContentProps = {
  attributes?: TimeSignalAttribute[];
  entry: SplitEntry;
  position: number;
  note: TimeSignal;
};

type StyleProps = {
  sourcesCount: number;
};

const useStyles = makeStyles((theme) => ({
  accordian: {
    flexDirection: 'column'
  },
  mainContainer: {
    display: 'flex'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    paddingLeft: ({ sourcesCount }: StyleProps) =>
      sourcesCount > 0 ? theme.spacing(2.5) : 0
  },
  dateTime: {
    display: 'flex'
  },
  noteEditor: {
    borderRadius: theme.spacing(0.5),
    paddingBottom: theme.spacing(4)
  },
  confirmAllSuggestion: {
    marginTop: theme.spacing(2)
  }
}));

const useCustomLabelStyles = makeStyles((theme) => ({
  root: {
    marginLeft: theme.spacing(0),
    marginRight: theme.spacing(0),
    paddingBottom: theme.spacing(1)
  },
  text: {
    marginLeft: theme.spacing(0),
    marginRight: theme.spacing(0)
  }
}));

export const SplitEntryAccordionContent: React.FC<SplitEntryAccordionContentProps> = ({
  attributes,
  entry,
  position,
  note
}: SplitEntryAccordionContentProps) => {
  const classes = useStyles({ sourcesCount: note?.sources.length ?? 0 });
  const customLabelClasses = useCustomLabelStyles();
  const timesheetLabels = useMemo(() => entry.timesheetLabels ?? [], [
    entry.timesheetLabels
  ]);
  const { labelLoading, setIsFormError } = useSplitEntriesContext();
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();

  return (
    <AccordionDetails
      className={classes.accordian}
      data-testid={`accordian-content-position-${position}`}
    >
      <TimesheetLabelSuggestionContextProvider>
        <FormErrorDialogContextProvider variant={'splitNote'}>
          <div className={classes.mainContainer}>
            {note?.sources && <NoteSourcesTable sources={note?.sources} />}
            <div className={classes.container}>
              <div className={classes.noteEditor}>
                <SplitEntryDescription
                  entry={entry}
                  position={position}
                  setIsFormError={setIsFormError}
                />
              </div>
              <div className={classes.dateTime}>
                <TimerContextProvider>
                  <DateTimeContent entry={entry} position={position} />
                </TimerContextProvider>
              </div>
            </div>
          </div>
          <LabelsFormFieldContextProvider
            timesheetLabels={`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.TIMESHEET_LABELS}`}
            note={`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.NOTE}`}
          >
            <CustomLabels
              overrideClasses={customLabelClasses}
              fieldName={`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.CUSTOM_LABELS}`}
            />
            <AttributesContent attributes={attributes} />
            <TimesheetLabelsSplitContent
              fields={timesheetLabels}
              loading={labelLoading ?? false}
              position={position}
              setIsFormError={setIsFormError}
            />
          </LabelsFormFieldContextProvider>
        </FormErrorDialogContextProvider>
      </TimesheetLabelSuggestionContextProvider>
      {isSmartSuggestionEnabled && (
        <div className={classes.confirmAllSuggestion}>
          <BulkSuggestionAcceptAllActions
            customLabels={`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.CUSTOM_LABELS}`}
            timesheetLabels={`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.TIMESHEET_LABELS}`}
          />
        </div>
      )}
    </AccordionDetails>
  );
};
